<template>
  <div class="stops-timeline p-4" :key="language">
    <!-- Horizontal stops list -->
    <div v-if="stops.length > 0" class="overflow-x-auto pb-2">
      <div class="flex items-start gap-3 min-w-max">
        <div 
          v-for="(stop, index) in stops" 
          :key="stop.id"
          class="flex items-start gap-3"
        >
          <!-- Stop card -->
          <div
            class="stop-card group bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-800/80 rounded-lg border-2 border-gray-200 dark:border-gray-600 p-3 cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-md transition-all w-64 relative"
            @click="$emit('stopClick', stop)"
          >
            <!-- Actions overlay (hidden in demo mode) -->
            <div v-if="!isInDemoMode" class="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
              <button
                @click.stop="$emit('editStop', stop)"
                class="p-1.5 text-white bg-blue-500 hover:bg-blue-600 rounded shadow-md transition-all"
                title="Edit stop"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                </svg>
              </button>
              <button
                @click.stop="$emit('deleteStop', stop.id)"
                class="p-1.5 text-white bg-red-500 hover:bg-red-600 rounded shadow-md transition-all"
                title="Delete stop"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                </svg>
              </button>
            </div>

            <div class="flex items-start gap-2 mb-2">
              <!-- Order number with badge -->
              <div class="flex flex-col items-center gap-1">
                <div class="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-md">
                  {{ index + 1 }}
                </div>
                <!-- Start/Finish badges below number -->
                <span v-if="index === 0" class="flex-shrink-0 inline-flex items-center bg-gradient-to-r from-green-500 to-emerald-600 text-white px-1.5 py-0.5 rounded-full text-xs font-bold shadow-md">
                  🏁
                </span>
                <span v-if="index === stops.length - 1 && stops.length > 1" class="flex-shrink-0 inline-flex items-center bg-gradient-to-r from-red-500 to-rose-600 text-white px-1.5 py-0.5 rounded-full text-xs font-bold shadow-md">
                  🎯
                </span>
              </div>

              <!-- Stop info -->
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1 flex-wrap">
                  <span class="text-lg">{{ getStopIcon(stop.type) }}</span>
                  <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {{ stop.name }}
                  </h4>
                </div>
                
                <p class="text-xs text-gray-600 dark:text-gray-400 truncate mb-2">
                  📍 {{ stop.address }}
                </p>
                
                <div class="flex items-center gap-2 text-xs">
                  <span class="inline-flex items-center gap-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-1 rounded-full font-medium">
                    <span class="flex-shrink-0">⏱️</span>
                    <span>{{ stop.duration }}min</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Arrow with distance/time (not for last stop) -->
          <div v-if="index < stops.length - 1" class="flex flex-col items-center justify-center px-2 self-stretch">
            <div class="text-gray-400 mb-1">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
              </svg>
            </div>
            <!-- Distance and time between stops -->
            <div v-if="calculation" class="text-center">
              <div class="text-xs font-semibold text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded-full mb-1">
                🛣️ {{ Math.round(getSegmentDistance(index) / 1000) }}km
              </div>
              <div class="text-xs font-semibold text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded-full">
                ⏱️ {{ formatSegmentDuration(index) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No stops message -->
    <div v-else class="text-center py-8">
      <div class="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800/50 dark:to-slate-800/50 rounded-lg p-8 border-2 border-dashed border-gray-300 dark:border-gray-600">
        <div class="text-5xl mb-4">🗺️</div>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          {{ t('noStops') }}
        </h3>
        <p class="text-gray-600 dark:text-gray-400 text-sm">
          {{ t('createFirstTrip') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Stop, TripCalculation } from '@/types'
import { useI18n } from '@/i18n'
import { useSettingsStore } from '@/stores/settings'
import { useAuthStore } from '@/stores/auth'
import { storeToRefs } from 'pinia'
import { formatDuration } from '@/utils/format'

interface Props {
  stops: Stop[]
  calculation?: TripCalculation | null
}

const props = withDefaults(defineProps<Props>(), {
  calculation: null
})

defineEmits<{
  'stopClick': [stop: Stop]
  'editStop': [stop: Stop]
  'deleteStop': [stopId: string]
}>()

const { t } = useI18n()
const settingsStore = useSettingsStore()
const authStore = useAuthStore()
const { language } = storeToRefs(settingsStore)

const isInDemoMode = computed(() => !authStore.isAuthenticated)

function getStopIcon(type: string): string {
  const icons: Record<string, string> = {
    city: '🏙️',
    gasStation: '⛽',
    evCharging: '⚡',
    restaurant: '🍽️',
    hotel: '🏨',
    attraction: '🎡',
    parking: '🅿️',
    rest: '😴',
    custom: '📍'
  }
  return icons[type] || '📍'
}

function getSegmentDistance(index: number): number {
  if (!props.calculation?.segments || index >= props.calculation.segments.length) {
    return 0
  }
  return props.calculation.segments[index].distance
}

function formatSegmentDuration(index: number): string {
  if (!props.calculation?.segments || index >= props.calculation.segments.length) {
    return '0m'
  }

  return formatDuration(props.calculation.segments[index].duration)
}
</script>

<style scoped>
.stops-timeline {
  background: var(--ion-background-color, #fff);
  border-bottom: 1px solid var(--ion-border-color, #ddd);
  /* Enable horizontal scrolling */
  scrollbar-width: thin;
  scrollbar-color: rgba(59, 130, 246, 0.5) transparent;
}

.stops-timeline::-webkit-scrollbar {
  height: 8px;
}

.stops-timeline::-webkit-scrollbar-track {
  background: transparent;
}

.stops-timeline::-webkit-scrollbar-thumb {
  background-color: rgba(59, 130, 246, 0.5);
  border-radius: 4px;
}

.stops-timeline::-webkit-scrollbar-thumb:hover {
  background-color: rgba(59, 130, 246, 0.7);
}

.stop-card {
  min-width: 16rem;
  max-width: 16rem;
}
</style>
